/**
 * PM Notification Bell Component
 * Modern Lit-based notification bell with real-time updates and dropdown
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export interface Notification {
  id: string;
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  type: 'tournament' | 'invitation' | 'match' | 'general';
  actionUrl?: string;
}

@customElement('pm-notification-bell')
export class PmNotificationBell extends LitElement {
  
  // Disable Shadow DOM to inherit global styles (FontAwesome)
  protected createRenderRoot() {
    return this;
  }

  // Properties
  @property({ type: Number }) count = 0;
  @property({ type: String }) variant: 'desktop' | 'mobile' = 'desktop';
  @property({ type: String, attribute: 'api-url' }) apiUrl = '/api/notifications/';
  
  // Internal state
  @state() private notifications: Notification[] = [];
  @state() private isOpen = false;
  @state() private loading = false;

  private bellRef?: HTMLButtonElement;

  connectedCallback() {
    super.connectedCallback();
    this.loadNotifications();
    this.setupClickOutside();
    this.injectStyles();
  }

  // Inject component styles when not using Shadow DOM
  private injectStyles() {
    if (document.getElementById('pm-notification-bell-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'pm-notification-bell-styles';
    style.textContent = this.getComponentStyles();
    document.head.appendChild(style);
  }

  private getComponentStyles(): string {
    return `
      pm-notification-bell {
        position: relative;
        display: inline-block;
      }

      pm-notification-bell .bell-button {
        position: relative;
        background: transparent;
        border: 1px solid rgba(255, 255, 255, 0.5);
        border-radius: 6px;
        width: 38px;
        height: 38px;
        padding: 0;
        color: white;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      pm-notification-bell .bell-button:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.5);
        transform: translateY(-1px);
      }

      pm-notification-bell .bell-button:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.3);
      }

      pm-notification-bell .bell-icon {
        font-size: 1rem;
        transition: transform 0.2s ease;
      }

      pm-notification-bell .bell-button:hover .bell-icon {
        transform: rotate(15deg);
      }

      pm-notification-bell .notification-badge {
        position: absolute;
        top: -5px;
        right: -8px;
        background: #dc3545;
        color: white;
        border-radius: 50%;
        min-width: 20px;
        height: 20px;
        font-size: 0.75rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 600;
        border: 1px solid var(--bs-body-bg);
        animation: pm-notification-pulse 2s infinite;
      }

      @keyframes pm-notification-pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.1); }
      }

      pm-notification-bell .notification-badge.hidden {
        display: none;
      }

      pm-notification-bell .dropdown-menu {
        position: absolute;
        top: calc(100% + 8px);
        right: 0;
        background: var(--bs-body-bg);
        border: 1px solid var(--bs-border-color);
        border-radius: 12px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
        min-width: 320px;
        max-width: 400px;
        max-height: 400px;
        overflow: hidden;
        z-index: 1050;
        opacity: 0;
        transform: translateY(-10px) scale(0.95);
        transition: all 0.2s ease;
        pointer-events: none;
      }

      pm-notification-bell .dropdown-menu.show {
        opacity: 1;
        transform: translateY(0) scale(1);
        pointer-events: auto;
      }

      pm-notification-bell .dropdown-header {
        padding: 16px 20px;
        border-bottom: 1px solid var(--bs-border-color);
        background: var(--bs-light);
      }

      pm-notification-bell .dropdown-header h6 {
        margin: 0;
        font-weight: 600;
        color: var(--bs-dark);
      }

      pm-notification-bell .notifications-list {
        max-height: 300px;
        overflow-y: auto;
      }

      pm-notification-bell .notification-item {
        padding: 12px 20px;
        border-bottom: 1px solid var(--bs-border-color);
        cursor: pointer;
        transition: background-color 0.2s ease;
        position: relative;
      }

      pm-notification-bell .notification-item:hover {
        background: var(--bs-light);
      }

      pm-notification-bell .notification-item:last-child {
        border-bottom: none;
      }

      pm-notification-bell .notification-item.unread {
        background: rgba(0, 123, 255, 0.05);
        border-left: 3px solid var(--bs-primary);
      }

      pm-notification-bell .notification-content {
        display: flex;
        gap: 12px;
        align-items: flex-start;
      }

      pm-notification-bell .notification-icon {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.875rem;
        flex-shrink: 0;
      }

      pm-notification-bell .notification-icon.tournament {
        background: rgba(255, 193, 7, 0.2);
        color: #f57c00;
      }

      pm-notification-bell .notification-icon.invitation {
        background: rgba(0, 123, 255, 0.2);
        color: #0056b3;
      }

      pm-notification-bell .notification-icon.match {
        background: rgba(40, 167, 69, 0.2);
        color: #155724;
      }

      pm-notification-bell .notification-icon.general {
        background: rgba(108, 117, 125, 0.2);
        color: #495057;
      }

      pm-notification-bell .notification-text {
        flex: 1;
        min-width: 0;
      }

      pm-notification-bell .notification-title {
        font-weight: 600;
        font-size: 0.875rem;
        line-height: 1.3;
        margin-bottom: 4px;
        color: var(--bs-dark);
      }

      pm-notification-bell .notification-message {
        font-size: 0.8rem;
        color: var(--bs-secondary);
        line-height: 1.4;
        margin-bottom: 4px;
      }

      pm-notification-bell .notification-time {
        font-size: 0.75rem;
        color: var(--bs-muted);
      }

      pm-notification-bell .dropdown-footer {
        padding: 12px 20px;
        background: var(--bs-light);
        text-align: center;
        border-top: 1px solid var(--bs-border-color);
      }

      pm-notification-bell .view-all-link {
        color: var(--bs-primary);
        text-decoration: none;
        font-size: 0.875rem;
        font-weight: 500;
      }

      pm-notification-bell .view-all-link:hover {
        text-decoration: underline;
      }

      pm-notification-bell .empty-state {
        padding: 40px 20px;
        text-align: center;
        color: var(--bs-muted);
      }

      pm-notification-bell .empty-state i {
        font-size: 2rem;
        margin-bottom: 12px;
        opacity: 0.5;
      }

      /* Mobile responsive */
      @media (max-width: 300px) {
        pm-notification-bell .dropdown-menu {
          right: -16px;
          left: -16px;
          min-width: auto;
          max-width: none;
        }
      }

      /* Dark theme support */
      [data-bs-theme="dark"] pm-notification-bell .bell-button {
        border-color: rgba(255, 255, 255, 0.2);
        color: #e9ecef;
      }

      [data-bs-theme="dark"] pm-notification-bell .bell-button:hover {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(255, 255, 255, 0.3);
      }

      [data-bs-theme="dark"] pm-notification-bell .dropdown-menu {
        background: var(--bs-dark);
        border-color: var(--bs-border-color);
      }
        
      [data-bs-theme="dark"] pm-notification-bell .dropdown-header {
        background: var(--bs-dark);
        border-bottom-color: var(--bs-border-color);
      }
      [data-bs-theme="dark"] pm-notification-bell .dropdown-header h6 {
        color: var(--bs-light);
      }

      [data-bs-theme="dark"] pm-notification-bell .notification-item {
        border-bottom-color: var(--bs-border-color);
      }

      [data-bs-theme="dark"] pm-notification-bell .notification-item:hover {
        background: var(--bs-secondary-bg);
      }

      [data-bs-theme="dark"] pm-notification-bell .notification-item.unread {
        background: rgba(0, 123, 255, 0.1);
      }
        
      [data-bs-theme="dark"] pm-notification-bell .notification-title {
        color: var(--bs-light);
      }

      [data-bs-theme="dark"] pm-notification-bell .dropdown-footer {
        background: var(--bs-dark);
        border-bottom-color: var(--bs-border-color);
      }
    `;
  }

  // Trigger re-render when internal state changes
  private triggerUpdate() {
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleClickOutside);
  }

  private setupClickOutside() {
    document.addEventListener('click', this.handleClickOutside);
  }

  private handleClickOutside = (event: Event) => {
    if (!this.contains(event.target as Node)) {
      this.isOpen = false;
    }
  };

  private async loadNotifications() {
    this.loading = true;
    try {
      // Mock data for now - replace with real API call
      await this.delay(500);
      this.notifications = [
        {
          id: '1',
          title: 'Nuovo Torneo Disponibile',
          message: 'È stato creato un nuovo torneo di padel nel tuo club preferito.',
          timestamp: new Date(Date.now() - 1000 * 60 * 5),
          read: false,
          type: 'tournament',
          actionUrl: '/tournaments/123'
        },
        {
          id: '2',
          title: 'Invito Partner Ricevuto',
          message: 'Marco Rossi ti ha invitato come partner per il torneo di sabato.',
          timestamp: new Date(Date.now() - 1000 * 60 * 30),
          read: false,
          type: 'invitation',
          actionUrl: '/invitations/'
        },
        {
          id: '3',
          title: 'Match Programmato',
          message: 'Il tuo match è previsto per domani alle 15:00.',
          timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
          read: true,
          type: 'match',
          actionUrl: '/matches/456'
        }
      ];
      this.count = this.notifications.filter(n => !n.read).length;
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      this.loading = false;
      this.triggerUpdate();
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private toggleDropdown() {
    this.isOpen = !this.isOpen;
    this.triggerUpdate();
    if (this.isOpen && this.notifications.length === 0) {
      this.loadNotifications();
    }
  }

  private handleNotificationClick(notification: Notification) {
    if (!notification.read) {
      notification.read = true;
      this.count = Math.max(0, this.count - 1);
      this.triggerUpdate();
    }
    
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
    
    this.isOpen = false;
    this.triggerUpdate();
  }

  private getNotificationIcon(type: string): string {
    const icons = {
      tournament: 'fas fa-trophy',
      invitation: 'fas fa-user-friends',
      match: 'fas fa-table-tennis-paddle-ball',
      general: 'fas fa-info-circle'
    };
    return icons[type as keyof typeof icons] || icons.general;
  }

  private formatTime(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - timestamp.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Ora';
    if (minutes < 60) return `${minutes}m fa`;
    if (hours < 24) return `${hours}h fa`;
    return `${days}g fa`;
  }

  render() {
    const hasUnread = this.count > 0;
    
    return html`
      <button 
        class="bell-button"
        @click=${this.toggleDropdown}
        aria-label="Notifiche"
        title="Notifiche"
      >
        <i class="bell-icon fas fa-bell"></i>
        <span 
          class="notification-badge ${hasUnread ? '' : 'hidden'}"
          aria-live="polite"
        >
          ${this.count}
        </span>
      </button>

      <div class="dropdown-menu ${this.isOpen ? 'show' : ''}">
        <div class="dropdown-header">
          <h6>Notifiche</h6>
        </div>

        ${this.notifications.length === 0 ? html`
          <div class="empty-state">
            <i class="fas fa-bell-slash"></i>
            <div>Nessuna notifica</div>
          </div>
        ` : html`
          <div class="notifications-list">
            ${this.notifications.map(notification => html`
              <div 
                class="notification-item ${notification.read ? '' : 'unread'}"
                @click=${() => this.handleNotificationClick(notification)}
              >
                <div class="notification-content">
                  <div class="notification-icon ${notification.type}">
                    <i class="${this.getNotificationIcon(notification.type)}"></i>
                  </div>
                  <div class="notification-text">
                    <div class="notification-title">${notification.title}</div>
                    <div class="notification-message">${notification.message}</div>
                    <div class="notification-time">${this.formatTime(notification.timestamp)}</div>
                  </div>
                </div>
              </div>
            `)}
          </div>

          <div class="dropdown-footer">
            <a href="/notifications/" class="view-all-link">
              Vedi tutte le notifiche
            </a>
          </div>
        `}
      </div>
    `;
  }

  // Public API methods
  public async refreshNotifications(): Promise<void> {
    await this.loadNotifications();
  }

  public addNotification(notification: Omit<Notification, 'id'>): void {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString()
    };
    
    this.notifications = [newNotification, ...this.notifications];
    if (!newNotification.read) {
      this.count++;
    }
    this.triggerUpdate();
  }

  public markAllAsRead(): void {
    this.notifications.forEach(n => n.read = true);
    this.count = 0;
    this.triggerUpdate();
  }

  public getUnreadCount(): number {
    return this.count;
  }
}